import React from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import type { GatewayRouteSloTarget } from "../../types";
import { SloMetricType } from "../../types";
import { SLO_METRIC_OPTIONS, SLO_TARGET_PLACEHOLDERS, sloMetricLabel } from "../../utils/sloMetricType";

const MAX_SLO_ROWS = 10;

export const emptySloRow = (): GatewayRouteSloTarget => ({
  metric: SloMetricType.LATENCY,
  sliDescription: "",
  sloTarget: "",
  measurementWindow: "",
  notes: "",
});

interface GatewayRouteSloSectionProps {
  rows: GatewayRouteSloTarget[];
  onChange: (rows: GatewayRouteSloTarget[]) => void;
}

const GatewayRouteSloSection: React.FC<GatewayRouteSloSectionProps> = ({ rows, onChange }) => {
  const updateRow = (index: number, patch: Partial<GatewayRouteSloTarget>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    onChange(rows.filter((_, i) => i !== index));
  };

  const addRow = () => {
    if (rows.length >= MAX_SLO_ROWS) return;
    onChange([...rows, emptySloRow()]);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Reliability targets (SLI / SLO)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Optional. Document latency, throughput, availability, or other expectations for this route.
      </Typography>
      {rows.map((row, index) => (
        <Box
          key={index}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "140px 1fr 1fr 120px 1fr auto" },
            gap: 1,
            mb: 1,
            alignItems: "start",
          }}
        >
          <TextField
            select
            size="small"
            label="Metric"
            value={row.metric}
            onChange={(e) =>
              updateRow(index, { metric: e.target.value as SloMetricType })
            }
          >
            {SLO_METRIC_OPTIONS.map((m) => (
              <MenuItem key={m} value={m}>
                {sloMetricLabel(m)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="SLI description"
            value={row.sliDescription ?? ""}
            onChange={(e) => updateRow(index, { sliDescription: e.target.value })}
          />
          <TextField
            size="small"
            label="SLO target"
            required
            value={row.sloTarget}
            placeholder={SLO_TARGET_PLACEHOLDERS[row.metric] ?? ""}
            onChange={(e) => updateRow(index, { sloTarget: e.target.value })}
          />
          <TextField
            size="small"
            label="Window"
            value={row.measurementWindow ?? ""}
            placeholder="30d"
            onChange={(e) => updateRow(index, { measurementWindow: e.target.value })}
          />
          <TextField
            size="small"
            label="Notes"
            value={row.notes ?? ""}
            onChange={(e) => updateRow(index, { notes: e.target.value })}
          />
          <IconButton aria-label="Remove SLO row" onClick={() => removeRow(index)} size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <Button size="small" onClick={addRow} disabled={rows.length >= MAX_SLO_ROWS}>
        Add SLO row
      </Button>
      {rows.length >= MAX_SLO_ROWS && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          Maximum {MAX_SLO_ROWS} targets
        </Typography>
      )}
    </Box>
  );
};

export default GatewayRouteSloSection;
